<template>
  <div>
    <v-container fluid class="pa-0 pb-8">
      <v-container class="container-custom">
        <Breadcrumbs v-if="!newPriceSuccess && !newPriceError"
          :title="$t('modules.coursesPrice.create.breadcrumbs.title')"
          :description="$t('modules.coursesPrice.create.breadcrumbs.subtitle')"
        />
      </v-container>
      <template v-if="!newPriceSuccess && !newPriceError">
        <v-container class="container-custom">
          <v-row justify="center">
            <v-col class="viewSpaces" sm="12">

              <OutlineCard
                :title="$t('modules.coursesPrice.create.section_product.title')"
                :subtitle="$t('modules.coursesPrice.create.section_product.subtitle')"
                :switchLabel="formattedCoursesStatus"
                switcher
                :switchValue="status"
                @switchChange="status = !status"
                class="mb-6"
              >
                <v-row>
                  <v-col sm="4" class="py-0">
                    <v-select
                      :error-messages="$v.selectedBillingProduct.$dirty && $v.selectedBillingProduct.$invalid ? $t('modules.coursesPrice.create.section_product.product_error'):''"
                      :items="filteredBillingProducts"
                      @blur="$v.selectedBillingProduct.$touch()"
                      class="mt-3"
                      item-text="name"
                      :label="$t('modules.coursesPrice.create.section_product.product')"
                      outlined
                      return-object
                      v-model="$v.selectedBillingProduct.$model"
                    />
                  </v-col>
                </v-row>
              </OutlineCard>

              <OutlineCard
                :title="$t('modules.coursesPrice.create.section_course.title')"
                :subtitle="$t('modules.coursesPrice.create.section_course.subtitle')"
                class="mb-6"
              >
                <TransferList
                  :firstListTitle="$t('modules.coursesPrice.create.section_course.transferList.firstTitle')"
                  :secondListTitle="$t('modules.coursesPrice.create.section_course.transferList.secondTitle')"
                  :availableItems.sync="availableCourses"
                  :addedItems.sync="$v.coursesAdded.$model"
                  ref="coursesAdded"
                />
                <v-alert
                  text
                  type="error"
                  class="mt-4"
                  v-if="$v.coursesAdded.$dirty && $v.coursesAdded.$anyError"
                >{{ $t('modules.coursesPrice.create.section_course.no-selected-warning') }}</v-alert>
              </OutlineCard>

              <OutlineCard
                :title="$t('modules.coursesPrice.create.section_career.title')"
                :subtitle="$t('modules.coursesPrice.create.section_career.subtitle')"
                class="mb-6"
              >
                <v-alert
                  v-if="!coursesAdded || coursesAdded.length === 0"
                  text
                  type="info"
                  class="mt-4"
                >
                  {{ $t('modules.coursesPrice.create.section_career.no_selected_course') }}
                </v-alert>
                <template v-else>
                  <TransferList
                    :firstListTitle="$t('modules.coursesPrice.create.section_career.transferList.firstTitle')"
                    :secondListTitle="$t('modules.coursesPrice.create.section_career.transferList.secondTitle')"
                    :availableItems.sync="availableCareers"
                    :addedItems.sync="$v.careersAdded.$model"
                    ref="careersAdded"
                  />
                  <v-alert
                    text
                    type="error"
                    class="mt-4"
                    v-if="$v.careersAdded.$dirty && $v.careersAdded.$anyError"
                  >{{ $t('modules.coursesPrice.create.section_career.no-selected-warning') }}</v-alert>
                </template>
              </OutlineCard>

              <OutlineCard :title="$t('modules.coursesPrice.create.section_student.title')" class="mb-6">
                <div>
                  <div class="d-flex">
                    <v-checkbox
                      :key="index"
                      :label="studentType.meaning"
                      :value="studentType"
                      class="mr-8 mt-0 no-message"
                      color="primary"
                      v-for="(studentType, index) in studentTypes"
                      v-model="$v.selectedStudentType.$model"
                    />
                  </div>
                  <v-alert
                    text
                    type="error"
                    v-if="$v.selectedStudentType.$anyError"
                  >{{$t('modules.coursesPrice.create.section_student.error')}}</v-alert>
                </div>
              </OutlineCard>

              <OutlineCard :title="$t('modules.coursesPrice.create.section_modality.title')" class="mb-6">
                <div>
                  <div class="d-flex">
                    <v-checkbox
                      :key="index"
                      :label="modalityType.meaning"
                      :value="modalityType"
                      class="mr-8 mt-0"
                      color="primary"
                      v-for="(modalityType, index) in modalityTypes"
                      v-model="$v.selectedModalityTypes.$model"
                    />
                  </div>
                  <v-alert
                    text
                    type="error"
                    v-if="$v.selectedModalityTypes.$anyError"
                  >{{$t('modules.coursesPrice.create.section_modality.error')}}</v-alert>
                </div>
              </OutlineCard>

              <OutlineCard
                :title="$t('modules.coursesPrice.create.section_periods.title')"
                :subtitle="$t('modules.coursesPrice.create.section_periods.subtitle')"
                class="mb-6"
              >
                <TransferList
                  :firstListTitle="$t('modules.coursesPrice.create.section_periods.transferList.firstTitle')"
                  :secondListTitle="$t('modules.coursesPrice.create.section_periods.transferList.secondTitle')"
                  :availableItems.sync="terms"
                  :added-items.sync="$v.termsAdded.$model"
                  ref="termsAdded"
                  :maxSelected="1"
                                  />
                <v-alert
                  v-if="$v.termsAdded.$dirty && $v.termsAdded.$anyError"
                  text type="error"
                  class="mt-4"
                >
                  {{ $t('modules.coursesPrice.create.section_periods.no-selected-warning') }}
                </v-alert>
              </OutlineCard>

              <OutlineCard :title="$t('modules.coursesPrice.create.section_zone.title')" class="mb-6">
                <v-row no-gutters>
                  <v-col sm="8">
                    <MultipleFormGroup
                      :items="$v.groups.$each.$iter"
                      :length="groups.length"
                      @addGroup="addGroup"
                      @removeGroup="removeGroup"
                      canAddGroup
                    >
                      <template v-slot:default="{item}">
                        <v-container fluid class="pa-0">
                          <v-row>
                            <v-col class="pb-0" sm="6">
                              <v-select
                                :error-messages="(item.zone.$invalid && item.zone.$dirty && $t('modules.coursesPrice.create.section_zone.items.zone_error')) || '' "
                                :items="zonesAvailable(item.zone.$model ? item.zone.$model.id : null)"
                                :label="$t('modules.coursesPrice.create.section_zone.items.zone')"
                                @blur="item.zone.$touch()"
                                item-text="zoneDescription"
                                outlined
                                return-object
                                v-model="item.zone.$model"
                              />
                            </v-col>
                            <v-col class="pb-0" sm="4">
                              <v-text-field
                                :error-messages="(item.price.$invalid && item.price.$dirty && $t('modules.coursesPrice.create.section_zone.items.price_error')) || ''"
                                :label="$t('modules.coursesPrice.create.section_zone.items.price')"
                                :min="0"
                                outlined
                                type="number"
                                v-model="item.price.$model"
                              >
                                <template v-slot:prepend>
                                  <span>{{ $countryConstants.currency }}</span>
                                </template>
                              </v-text-field>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                    </MultipleFormGroup>
                  </v-col>
                </v-row>
              </OutlineCard>

              <div class="d-flex justify-end">
                <v-btn text rounded large class="cancelbutton mr-3" @click="goBack">{{$t('modules.coursesPrice.create.buttons.cancel')}}</v-btn>
                <Button
                  :disabled="!canCreate || $v.$invalid || !$v.selectedBillingProduct.$model || !$v.coursesAdded.$model || !$v.careersAdded.$model || !$v.termsAdded.$model"
                  :loading="sendingNewPrice"
                  :success="newPriceSuccess"
                  :error="newPriceError"
                  :errorText="$t('modules.coursesPrice.create.buttons.error')"
                  :text="$t('modules.coursesPrice.create.buttons.create')"
                  :successText="$t('modules.coursesPrice.create.buttons.created')"
                  @clicked="createCoursePrice"
                  class="crearbutton"
                  large
                  rounded
                  primary
                />
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <SuccessMessage
        v-else
        :title="successMessage.title"
        :type="successMessage.type"
        :actionPrimary="successMessage.actionPrimary"
      />
    </v-container>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs';
import OutlineCard from '@/components/OutlinedCard/OutlinedCard';
import TransferList from '@/components/TransferList/TransferListNew';
import MultipleFormGroup from '@/components/MultipleFormGroup/MultipleFormGroup';
import Button from '@/components/Button/Button';
import SuccessMessage from '@/components/SuccessMessage/SuccessMessage';
import { $careers, $course, $types, $zones, $terms, $extracurricularCourses, $products } from '../Services';
import { required } from 'vuelidate/lib/validators';

const arrayNotEmpty = array => array.length;

export default {
  components: {
    Breadcrumbs,
    OutlineCard,
    TransferList,
    MultipleFormGroup,
    Button,
    SuccessMessage
  },
  data() {
    return {
      userAccept: false,
      sendingNewPrice: false,
      newPriceError: false,
      newPriceSuccess: false,
      billingProducts: [],
      selectedBillingProduct: {},
      availableCourses: [],
      coursesAdded: [],
      availableCareers: [],
      careersAdded: [],
      studentTypes: [],
      modalityTypes: [],
      zones: [],
      terms: [],
      termsAdded: [],
      status: true,
      selectedModalityTypes: [],
      selectedStudentType: [],
      groups: [
        {
          zone: null,
          price: null,
        }
      ],
      successMessage: {
        title: '',
        type: '',
        actionPrimary: { text: '', callback: () => { this.$router.push('/courses') } },
      },
    }
  },
  validations: {
    selectedBillingProduct: { required },
    coursesAdded: { arrayNotEmpty },
    careersAdded: { arrayNotEmpty },
    selectedModalityTypes: { arrayNotEmpty },
    selectedStudentType: { arrayNotEmpty },
    termsAdded: { arrayNotEmpty },
    groups: {
      $each: {
        zone: { required },
        price: { required },
      }
    },
    formValid: ['selectedBillingProduct', 'coursesAdded', 'careersAdded', 'selectedModalityTypes', 'selectedStudentType', 'termsAdded', 'groups']
  },
  computed: {
    canCreate() {
      return this.$permissions?.portaladministrativo?.academic?.courses?.CREATE
    },
    formattedCoursesStatus() {
      return this.status ? this.$t('modules.coursesPrice.create.section_product.enabled') : this.$t('modules.coursesPrice.create.section_product.disabled')
    },
    filteredBillingProducts() {
      return this.billingProducts.filter(product => product.id === 248);
    }
  },
  watch: {
    coursesAdded: {
      handler(newCourses, oldCourses) {
        if (JSON.stringify(newCourses) !== JSON.stringify(oldCourses)) {
          this.getCareers();
        }
        this.$v.coursesAdded.$touch();
      },
      deep: true
    },
    careersAdded: {
      handler() {
        this.$v.careersAdded.$touch();
      },
      deep: true
    },
    termsAdded: {
      handler() {
        this.$v.termsAdded.$touch();
      },
      deep: true
    },
    selectedBillingProduct: {
      handler() {
        this.$v.selectedBillingProduct.$touch();
      },
      deep: true
    }
  },
  methods: {
    zonesAvailable(idZoneSelected) {
      return this.zones.filter(zone => {
        let found = false;
        this.groups.forEach(group => {
          if (group.zone && zone.zoneDescription === group.zone.zoneDescription && group.zone.id !== idZoneSelected) {
            found = true;
          }
        });
        return !found;
      });
    },
    goBack() {
      if (window.history.length > 1) {
        const getCurrentRoute = this.$route.matched[this.$route.matched.length - 1];
        if (getCurrentRoute.meta.name !== 'Index') {
          this.$router.go(-1)
        } else {
          this.$router.push('/')
        }
      }
    },
    removeGroup(index) {
      this.groups.splice(index, 1)
    },
    addGroup() {
      this.groups.push({ zone: null, price: null });
    },
    async createCoursePrice() {
      if (!this.canCreate) return false;
      if (this.status || this.userAccept) {
        this.sendingNewPrice = true;
        const priceDetails = [];
        this.selectedStudentType.forEach(studentType => {
          this.selectedModalityTypes.forEach(modalityType => {
            this.groups.forEach(group => {
              priceDetails.push({
                amount: Number(group.price),
                zone: {
                  id: group.zone.id,
                  zoneDescription: group.zone.zoneDescription
                },
                modalityType: {
                  value: modalityType.value,
                  meaning: modalityType.meaning
                },
                studentType: {
                  value: studentType.value,
                  meaning: studentType.meaning
                }
              });
            });
          });
        });
        const newCoursePrice = {
          status: this.status,
          termId: this.termsAdded[0].id,
          billingProduct: {
            id: this.selectedBillingProduct.id
          },
          academicCourseIds: this.coursesAdded.map(course => course.id),
          careerIds: this.careersAdded.map(career => career.id),
          priceDetails,
        };
        try {
          await $course.create(newCoursePrice,null,'create');
          this.successMessage.title = this.$t('modules.coursesPrice.create.success_title');
          this.successMessage.type = 'success';
          this.successMessage.actionPrimary = {
            text: this.$t('modules.coursesPrice.create.accept'),
            callback: () => { this.$router.push('/courses') }
          };
          this.newPriceSuccess = true;
        } catch (error) {
          this.successMessage.title = this.$t('modules.coursesPrice.create.error_title');
          this.successMessage.type = 'error';
          this.successMessage.actionPrimary.text = this.$t('modules.coursesPrice.create.go_to_back');
          this.successMessage.actionPrimary.callback = () => { 
            this.newPriceError = false;
            this.fetchData();
          }
          this.newPriceError = true;
        } finally {
          this.sendingNewPrice = false;
        }
      } else {
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('modules.coursesPrice.create.warning_popup.title'),
          content: [
            {
              value: this.$t('modules.coursesPrice.create.warning_popup.content')
            }
          ],
          actionPrimary: {
            text: this.$t('modules.coursesPrice.create.buttons.create'),
            callback: () => {
              this.userAccept = true;
              this.createCoursePrice();
            }
          },
          actionSecondary: {
            text: this.$t('modules.coursesPrice.create.buttons.cancel'),
            callback() { }
          },
          icon: { color: 'warning', name: 'mdi-eye-off-outline' },
          color: 'primary',
        });
      }
    },
    async getCourses() {
      try {
        const { data } = await $extracurricularCourses.find(null, null, {
          params: {
            page: 0,
            length: 100,
            orderBy: 'id',
            orientation: 'desc'
          }
        });
        this.availableCourses = data.content.map(item => {
          item.name = `(${item.code}) ${item.name}`;
          return item;
        });
      } catch (error) {
        this.availableCourses = [];
      }
    },
    async getCareers() {
      try {
        const { data } = await $careers.find(null, null, {
          params: {
            page: 0,
            length: 100,
            orderBy: 'id',
            orientation: 'desc',
            excludeStudyPlan: true
          }
        });
        this.availableCareers = data.content.map(item => {
          item.name = `(${item.code}) ${item.description}`;
          return item;
        });
      } catch (error) {
        this.availableCareers = [];
      }
    },
    async getTerms() {
      try {
        const { data } = await $terms.find(null, null, {
          params: {
            page: 0,
            length: 100,
            orderBy: 'classStartDate',
            orientation: 'desc',
          }
        });
        this.terms = data.content
        .filter(item => item.billingProducts && item.billingProducts.some(p => p.id === 248))
        .map(item => {
            item.name = `(${item.code}) ${item.description}`;
            return item;
          });
      } catch (error) {
        this.terms = [];
      }
    },
    async fetchData() {
      try {
        const [zones, studentType, modalityType, products] = await Promise.all([
          $zones.find(null, null, { params: { page: 0, length: 500, orderBy: 'id', orientation: 'desc' } }),
          $types.find(null, null, { params: { type: 'STUDENT_TYPE' } }),
          $types.find(null, null, { params: { type: 'MODALITY_TYPE' } }),
          $products.find(null, null, { params: { page: 0, length: 500, orderBy: 'id', orientation: 'desc' } }),
        ]);
        this.zones = zones.data.content;
        this.studentTypes = studentType.data;
        this.modalityTypes = modalityType.data;
        this.billingProducts = products.data.content;
        this.getCourses();
        this.getCareers();
        this.getTerms();
      } catch (error) {
        this.$store.dispatch('commons/openPopUp', {
          title: this.$t('alert'),
          content: [{ value: this.$t('modules.coursesPrice.create.data_loading_error') }],
          actionPrimary: { text: this.$t('actions.accept'), callback() { } },
          icon: { color: 'warning', name: 'mdi-alert' },
          color: 'primary',
        });
      }
    }
  },
  mounted() {
    this.fetchData();
  }
}
</script>
